import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { X, Edit2, Check, Copy } from 'lucide-react';
import { DeviceTemplateProps, TraitStyleConfig, AttributeStyleConfig, IconStyleId } from '../../types';

// DeviceIcon import
import DeviceIcon from '../../../deviceIcons';

// Import Dynamic Trait Renderer
import { DynamicTrait, DynamicAttribute, getTraitStyleId, getAttributeStyleId } from '../../DynamicTraitRenderer';

// Import attribute components (fallback)
import { Style02_Inline as SensorInline } from '../../attributes/SensorDisplay/Style02_Inline';
import { Style02_Area as SensorAreaChart } from '../../attributes/SensorChart/Style02_Area';

// Import API for self-fetching energy data
import { ultronTuringApi } from '../../../../services/ultronTuringApi';

/**
 * Switch Template 01 - Card Style
 * Full card layout with power monitoring
 * 
 * Merged Mode:
 * - Title uses deviceName (not nickname)
 * - Shows all endpoints with individual controls
 * - No nickname editing
 */
export const Template01_Card: React.FC<DeviceTemplateProps> = ({
  device,
  isExecutingCommand,
  onTraitControl,
  onClose,
  onNicknameSave,
  config,
  energyChartData = [],
  isLoadingEnergyChart = false,
  isMerged,
  endpointCount,
  onToggleMerge,
  allEndpoints
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState(device.nickname || device.deviceName);
  const blockSyncUntilRef = useRef<number>(0);
  const isSavingRef = useRef(false);

  // ✅ 自行獲取能源圖表數據 - 在 modal 打開時觸發
  const [selfFetchedChartData, setSelfFetchedChartData] = useState<Array<{ time: string; value: number }>>([]);
  const [isSelfLoading, setIsSelfLoading] = useState(false);
  const mountedRef = useRef(true);
  
  useEffect(() => {
    // 只有當設備有電力計量能力時才獲取數據
    if (device.watt === undefined && device.wattHour === undefined) {
      return;
    }
    
    mountedRef.current = true;
    
    const fetchEnergyData = async () => {
      const deviceSN = device.deviceId;
      console.log(`📊 [Switch Modal] Fetching energy data for: ${deviceSN}`);
      
      setIsSelfLoading(true);
      setSelfFetchedChartData([]); // Clear old data immediately
      
      try {
        const report = await ultronTuringApi.generateReport([deviceSN], {
          duration: '24h',
          granularity: 'hour',
          format: 'summary',
          utcOffset: '+8:00'
        });
        
        // Check if component is still mounted
        if (!mountedRef.current) {
          console.log(`📊 [Switch Modal] Component unmounted, ignoring response for: ${deviceSN}`);
          return;
        }
        
        if (report.success && report.data) {
          const reportData = report.data as Record<string, any>;
          const deviceData = reportData[deviceSN];
          
          if (deviceData?.chartData && Array.isArray(deviceData.chartData)) {
            // Helper to format time label
            const formatTimeLabel = (timeStr: string) => {
              const d = new Date(timeStr);
              const month = (d.getMonth() + 1).toString().padStart(2, '0');
              const day = d.getDate().toString().padStart(2, '0');
              const hour = d.getHours().toString().padStart(2, '0');
              return `${month}/${day} ${hour}:00`;
            };
            
            const chartData = deviceData.chartData
              .map((point: any) => ({
                time: point.time ? formatTimeLabel(point.time) : '',
                value: point.energy ?? point.wattHour ?? 0
              }))
              .sort((a: any, b: any) => new Date(a.time).getTime() - new Date(b.time).getTime());
            
            console.log(`📊 [Switch Modal] ✅ Got ${chartData.length} data points for: ${deviceSN}`);
            setSelfFetchedChartData(chartData);
          } else {
            console.log(`📊 [Switch Modal] ❌ No chartData found for: ${deviceSN}`);
          }
        }
      } catch (error) {
        console.error(`📊 [Switch Modal] Error fetching energy data:`, error);
      } finally {
        if (mountedRef.current) {
          setIsSelfLoading(false);
        }
      }
    };
    
    fetchEnergyData();
    
    // Cleanup: mark as unmounted to ignore stale responses
    return () => {
      mountedRef.current = false;
    };
  }, [device.deviceId, device.watt, device.wattHour]);
  
  // Use self-fetched data if available, otherwise fallback to prop
  const chartDataToUse = selfFetchedChartData.length > 0 ? selfFetchedChartData : energyChartData;
  const isChartLoading = isSelfLoading || isLoadingEnergyChart;

  // Extract styles from config
  const traitStyles: TraitStyleConfig = config?.traitStyles || {};
  const attributeStyles: AttributeStyleConfig = config?.attributeStyles || {};
  const smallIconStyle: IconStyleId = (config?.smallIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const bigIconStyle: IconStyleId = (config?.bigIconStyle || config?.iconStyle || 'Style01') as IconStyleId;
  const hiddenComponents: string[] = config?.hiddenComponents || [];
  // Unified order: Attributes + Traits (BigIcon is always fixed at top)
  const traitOrder: string[] = config?.traitOrder || ['OnOff', 'SensorDisplay', 'SensorChart'];

  // Sync tempName when device prop changes, but skip if we just saved
  useEffect(() => {
    if (!isEditingName && Date.now() > blockSyncUntilRef.current) {
      setTempName(device.nickname || device.deviceName);
    }
  }, [device.nickname, device.deviceName, isEditingName]);

  const handleSaveName = async () => {
    if (onNicknameSave && tempName.trim()) {
      isSavingRef.current = true;
      const savedName = tempName.trim();
      blockSyncUntilRef.current = Date.now() + 3000;
      await onNicknameSave(device.deviceId, savedName);
      setIsEditingName(false);
      isSavingRef.current = false;
    }
  };

  const handleBlur = () => {
    if (!isSavingRef.current) {
      setIsEditingName(false);
    }
  };

  const handleOnOff = () => {
    onTraitControl(device, 'OnOff', device.isOn ? 'off' : 'on');
  };

  // For merged mode: handle each endpoint's on/off
  const handleEndpointOnOff = (endpoint: any) => {
    onTraitControl(endpoint, 'OnOff', endpoint.isOn ? 'off' : 'on');
  };

  // Display title: use deviceName for merged, nickname for individual
  const displayTitle = isMerged ? device.deviceName : (device.nickname || device.deviceName);
  
  // Endpoints to display
  const endpointsToShow = isMerged && allEndpoints && allEndpoints.length > 1 ? allEndpoints : null;

  // Handle backdrop click - close modal or cancel editing
  const handleBackdropClick = useCallback(() => {
    if (isEditingName) {
      setIsEditingName(false);
      setTempName(device.nickname || device.deviceName || '');
    } else {
      onClose();
    }
  }, [isEditingName, device.nickname, device.deviceName, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4" onClick={handleBackdropClick}>
      <div className="bg-gradient-to-b from-[#0a1628] via-[#0d1d35] to-[#0a1628] rounded-3xl w-full max-w-sm max-h-[90vh] overflow-hidden shadow-2xl border border-slate-800/50" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <DeviceIcon 
              deviceType={device.deviceType as any} 
              isOn={device.isOn} 
              size={20} 
              styleId={smallIconStyle}
              className="flex-shrink-0"
            />
            {isEditingName ? (
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <input
                  type="text"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  className="flex-1 min-w-0 px-2 py-1 text-sm font-bold border border-slate-600 rounded bg-slate-800/80 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveName();
                    }
                    if (e.key === 'Escape') setIsEditingName(false);
                  }}
                  onBlur={handleBlur}
                />
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleSaveName}
                  className="p-1 text-green-500 hover:text-green-400 flex-shrink-0"
                  title="儲存"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => setIsEditingName(false)}
                  className="p-1 text-slate-400 hover:text-slate-300 flex-shrink-0"
                  title="取消"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-base font-bold text-white truncate">{displayTitle}</h2>
                {/* Edit button: merged mode edits deviceName, individual mode edits nickname */}
                {onNicknameSave && (
                  <button 
                    onClick={() => {
                      // Merged mode: edit deviceName; Individual mode: edit nickname
                      setTempName(isMerged ? device.deviceName : (device.nickname || device.deviceName));
                      setIsEditingName(true);
                    }}
                    className="p-1 hover:bg-slate-700/50 rounded transition-colors flex-shrink-0"
                  >
                    <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                  </button>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!hiddenComponents.includes('StatusIndicator') && (
              <DynamicAttribute
                attributeName="StatusIndicator"
                styleId={getAttributeStyleId('StatusIndicator', attributeStyles)}
                componentProps={{
                  online: device.status === 'online',
                  label: device.status === 'online' ? 'ONLINE' : 'OFFLINE'
                }}
              />
            )}
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-slate-700/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 pb-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Big Icon */}
          {!hiddenComponents.includes('BigIcon') && (
            <div className="flex justify-center">
              <div className={`w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300 ${
                device.isOn 
                  ? 'bg-gradient-to-br from-blue-500/30 to-cyan-500/30 border-2 border-blue-400/50 shadow-lg shadow-blue-500/30' 
                  : 'bg-slate-800/50 border-2 border-slate-700'
              }`}>
                <DeviceIcon
                  deviceType={device.deviceType as any}
                  isOn={device.isOn}
                  size={64}
                  styleId={bigIconStyle}
                  className={device.isOn ? 'text-blue-400' : 'text-slate-500'}
                />
              </div>
            </div>
          )}

          {/* Merged Mode: Show all endpoints */}
          {endpointsToShow ? (
            <div className="space-y-3">
              {endpointsToShow.map((endpoint: any) => {
                const endpointLabel = endpoint.nickname || endpoint.deviceId?.split('#')[1] || endpoint.deviceId;
                return (
                  <div key={endpoint.deviceId} className="bg-slate-800/30 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DeviceIcon 
                          deviceType={device.deviceType as any} 
                          isOn={endpoint.isOn} 
                          size={16} 
                          styleId={smallIconStyle}
                          className="flex-shrink-0"
                        />
                        <span className="text-sm text-slate-300">{endpointLabel}</span>
                      </div>
                      <DynamicTrait
                        traitName="OnOff"
                        styleId={getTraitStyleId('OnOff', traitStyles)}
                        componentProps={{
                          isOn: endpoint.isOn ?? false,
                          onToggle: () => handleEndpointOnOff(endpoint),
                          disabled: isExecutingCommand,
                          loading: isExecutingCommand,
                          label: ""
                        }}
                      />
                    </div>
                    {endpoint.watt !== undefined && (
                      <div className="mt-2 flex items-center justify-between text-xs">
                        <span className="text-slate-500">功率</span>
                        <span className="text-green-400 font-medium">{endpoint.watt} W</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              {/* Single endpoint mode - Dynamically rendered components based on traitOrder */}
              {traitOrder.map((componentName) => {
                if (hiddenComponents.includes(componentName)) return null;
                
                switch (componentName) {
                  case 'OnOff':
                    return (
                      <DynamicTrait
                        key="OnOff"
                        traitName="OnOff"
                        styleId={getTraitStyleId('OnOff', traitStyles)}
                        componentProps={{
                          isOn: device.isOn ?? false,
                          onToggle: handleOnOff,
                          disabled: isExecutingCommand,
                          loading: isExecutingCommand,
                          label: "電源"
                        }}
                      />
                    );
                  
                  case 'SensorDisplay':
                    if (device.watt === undefined && device.voltage === undefined && device.current === undefined) return null;
                    const hasBoth = device.voltage !== undefined && device.current !== undefined;
                    return (
                      <div key="SensorDisplay" className="space-y-3">
                        {device.watt !== undefined && (
                          <DynamicAttribute
                            attributeName="SensorDisplay"
                            styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                            componentProps={{
                              type: "power",
                              value: device.watt,
                              label: "目前功率"
                            }}
                          />
                        )}
                        {(device.voltage !== undefined || device.current !== undefined) && (
                          <div className={hasBoth ? "grid grid-cols-2 gap-3" : "flex justify-center"}>
                            {device.voltage !== undefined && (
                              <DynamicAttribute
                                attributeName="SensorDisplay"
                                styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                                componentProps={{
                                  type: "voltage",
                                  value: device.voltage,
                                  label: "電壓"
                                }}
                              />
                            )}
                            {device.current !== undefined && (
                              <DynamicAttribute
                                attributeName="SensorDisplay"
                                styleId={getAttributeStyleId('SensorDisplay', attributeStyles)}
                                componentProps={{
                                  type: "current",
                                  value: device.current > 100 ? device.current / 1000 : device.current,
                                  label: "電流"
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  
                  case 'SensorChart':
                    if (device.watt === undefined && device.wattHour === undefined) return null;
                    return (
                      <DynamicAttribute
                        key="SensorChart"
                        attributeName="SensorChart"
                        styleId={getAttributeStyleId('SensorChart', attributeStyles)}
                        componentProps={{
                          data: chartDataToUse,
                          dataKey: "value",
                          loading: isChartLoading,
                          title: "過去 24 小時用電量",
                          color: "#22c55e",
                          unit: "kWh"
                        }}
                      />
                    );
                  
                  default:
                    return null;
                }
              })}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-800/50 bg-slate-900/30 space-y-2">
          {/* Multi-endpoint device merge toggle - only for multi_channel devices */}
          {device.isMultiChannel && endpointCount && endpointCount > 1 && onToggleMerge && (
            <div className="flex items-center justify-between py-1 px-2 bg-slate-700/30 rounded-lg">
              <span className="text-xs text-slate-400">
                多端點設備 ({endpointCount} 個)
              </span>
              <button
                onClick={() => onToggleMerge(device.baseSN)}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  isMerged 
                    ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' 
                    : 'bg-slate-600/50 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {isMerged ? '顯示為個別方塊' : '顯示為單一方塊'}
              </button>
            </div>
          )}
          
          {/* Location & SN with copy */}
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{device.location || device.groupName || ''}</span>
            <div className="flex items-center gap-1">
              <span className="font-mono">{isMerged ? device.baseSN : (device.deviceId || device.baseSN)}</span>
              <button
                onClick={() => navigator.clipboard.writeText(isMerged ? device.baseSN : (device.deviceId || device.baseSN))}
                className="text-slate-400 hover:text-white transition-colors"
                title="複製設備 SN"
              >
                <Copy className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Template01_Card;
